import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Send, Bell } from 'lucide-react';

interface Customer {
  id: string;
  full_name: string;
  email: string;
}

interface PushNotificationManagerProps {
  customers?: Customer[];
  tenantId?: string;
}

export function PushNotificationManager({ customers = [], tenantId }: PushNotificationManagerProps) {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [sendToAll, setSendToAll] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [lastResult, setLastResult] = useState<any>(null);
  const { toast } = useToast();

  const handleSendNotification = async () => {
    if (!title.trim() || !message.trim()) {
      toast({
        title: "Fehler",
        description: "Titel und Nachricht sind erforderlich",
        variant: "destructive"
      });
      return;
    }

    if (!sendToAll && !selectedCustomer) {
      toast({
        title: "Fehler", 
        description: "Bitte wählen Sie einen Kunden aus",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    setLastResult(null);

    try {
      const payload = {
        title: title.trim(),
        body: message.trim(),
        data: {
          timestamp: Date.now(),
          source: 'admin'
        },
        ...(sendToAll 
          ? { send_to_all: true, tenant_id: tenantId }
          : { user_id: selectedCustomer }
        )
      };

      const { data, error } = await supabase.functions.invoke('send-push-notification', {
        body: payload
      });

      if (error) throw error;

      setLastResult(data);
      
      toast({
        title: "Erfolgreich gesendet!",
        description: `Benachrichtigung an ${sendToAll ? 'alle Kunden' : '1 Kunde'} gesendet`,
      });

      // Reset form
      setTitle('');
      setMessage('');
      setSelectedCustomer('');

    } catch (error: any) {
      console.error('Error sending push notification:', error);
      toast({
        title: "Fehler beim Senden",
        description: error.message || "Unbekannter Fehler beim Senden der Benachrichtigung",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Push-Benachrichtigungen verwalten
        </CardTitle>
        <CardDescription>
          Senden Sie Push-Benachrichtigungen an Ihre Kunden
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="title">Titel</Label>
          <Input
            id="title"
            placeholder="Benachrichtigung Titel"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={50}
          />
          <p className="text-sm text-muted-foreground">{title.length}/50 Zeichen</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="message">Nachricht</Label>
          <Textarea
            id="message"
            placeholder="Ihre Nachricht hier..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            maxLength={200}
          />
          <p className="text-sm text-muted-foreground">{message.length}/200 Zeichen</p>
        </div>

        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Switch
              id="send-to-all"
              checked={sendToAll}
              onCheckedChange={setSendToAll}
            />
            <Label htmlFor="send-to-all">An alle Kunden senden</Label>
          </div>

          {!sendToAll && (
            <div className="space-y-2">
              <Label htmlFor="customer-select">Kunde auswählen</Label>
              <Select value={selectedCustomer} onValueChange={setSelectedCustomer}>
                <SelectTrigger>
                  <SelectValue placeholder="Wählen Sie einen Kunden aus" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((customer) => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.full_name} ({customer.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {lastResult && (
          <Alert>
            <AlertDescription>
              <strong>Letztes Ergebnis:</strong> {lastResult.message}<br />
              Erfolgreich: {lastResult.successful}, Fehlgeschlagen: {lastResult.failed}, Gesamt: {lastResult.total}
            </AlertDescription>
          </Alert>
        )}

        <Button 
          onClick={handleSendNotification} 
          disabled={isLoading || !title.trim() || !message.trim()}
          className="w-full"
        >
          <Send className="h-4 w-4 mr-2" />
          {isLoading 
            ? 'Sende...' 
            : `Benachrichtigung senden ${sendToAll ? 'an alle' : 'an ausgewählten Kunden'}`
          }
        </Button>

        <div className="text-sm text-muted-foreground space-y-1">
          <p><strong>Hinweise:</strong></p>
          <ul className="list-disc list-inside space-y-1">
            <li>Benachrichtigungen werden nur an Kunden gesendet, die Push-Benachrichtigungen aktiviert haben</li>
            <li>Der Titel sollte kurz und aussagekräftig sein</li>
            <li>Nachrichten können bis zu 200 Zeichen lang sein</li>
            <li>Benachrichtigungen werden sofort zugestellt</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}